import React, { createContext, useContext, useState, useEffect } from 'react';

const CountryContext = createContext();

const SUPPORTED_COUNTRIES = {
  ghana: { code: 'ghana', name: 'Ghana', flag: '🇬🇭', flagUrl: 'https://flagcdn.com/w20/gh.png', currency: 'GHS' },
  nigeria: { code: 'nigeria', name: 'Nigeria', flag: '🇳🇬', flagUrl: 'https://flagcdn.com/w20/ng.png', currency: 'NGN' }
};

export const CountryProvider = ({ children }) => {
  const [country, setCountryState] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('smartpos_country');
    if (saved && SUPPORTED_COUNTRIES[saved]) {
      return saved;
    }
    return null; // Will be detected
  });
  const [detecting, setDetecting] = useState(!country);

  useEffect(() => {
    if (country) return; // Already set

    const detectCountry = async () => {
      try {
        // Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        const countryCode = data.country_code?.toLowerCase();

        if (countryCode === 'ng') {
          setCountryState('nigeria');
          localStorage.setItem('smartpos_country', 'nigeria');
        } else if (countryCode === 'gh') {
          setCountryState('ghana');
          localStorage.setItem('smartpos_country', 'ghana');
        } else {
          // Default to Ghana for other countries
          setCountryState('ghana');
          localStorage.setItem('smartpos_country', 'ghana');
        }
      } catch (error) {
        console.error('Failed to detect country:', error);
        // Default to Ghana on error
        setCountryState('ghana');
        localStorage.setItem('smartpos_country', 'ghana');
      } finally {
        setDetecting(false);
      }
    };

    detectCountry();
  }, [country]);

  const setCountry = (newCountry) => {
    if (SUPPORTED_COUNTRIES[newCountry]) {
      setCountryState(newCountry);
      localStorage.setItem('smartpos_country', newCountry);
    }
  };

  const currentCountry = SUPPORTED_COUNTRIES[country] || SUPPORTED_COUNTRIES.ghana;

  return (
    <CountryContext.Provider value={{
      country,
      setCountry,
      currentCountry,
      detecting,
      supportedCountries: SUPPORTED_COUNTRIES
    }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export default CountryContext;
